// ────────────────────────────────────────────────────────────────────────────
//  theme.ts — Light/Dark/System Theme-Manager
//
//  Setzt `data-theme="light"` oder `data-theme="dark"` aufs <html>-Element.
//  Speichert User-Wahl in localStorage. „System" folgt prefers-color-scheme.
//
//  Die CSS-Variablen sind in src/index.css definiert — alle theme-aware
//  Components nutzen die.
// ────────────────────────────────────────────────────────────────────────────

export type ThemeChoice = 'light' | 'dark' | 'system'

const KEY = 'custosoft.theme'

const listeners = new Set<(t: ThemeChoice) => void>()

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function getThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'dark'
  const v = localStorage.getItem(KEY)
  return (v === 'light' || v === 'dark' || v === 'system') ? v : 'system'
}

/// Aktiv aufgelöstes Theme (system → light/dark gemäß OS-Präferenz)
export function getResolvedTheme(): 'light' | 'dark' {
  const choice = getThemeChoice()
  return choice === 'system' ? getSystemTheme() : choice
}

export function setThemeChoice(choice: ThemeChoice) {
  localStorage.setItem(KEY, choice)
  applyTheme()
  listeners.forEach(fn => fn(choice))
}

/// Schreibt das aufgelöste Theme aufs <html>. Wird beim App-Boot + bei jedem
/// setThemeChoice() aufgerufen. Außerdem reagieren wir auf System-Changes.
export function applyTheme() {
  if (typeof document === 'undefined') return
  const resolved = getResolvedTheme()
  document.documentElement.dataset.theme = resolved
  // theme-color für Browser-Chrome (Mobile-Adressleiste)
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', resolved === 'light' ? '#eef2ff' : '#06060e')
}

/// Boot-Hook: einmalig beim App-Start aufrufen.
export function initTheme() {
  applyTheme()
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    mq.addEventListener('change', () => {
      if (getThemeChoice() === 'system') applyTheme()
    })
  }
}

/// React-friendly Subscribe: für ein Hook
export function subscribeTheme(fn: (t: ThemeChoice) => void) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
